import { searchProcoreObjects } from "apis/procore/bimObjectSearch";
// Deep imports: the `apis/procore` barrel pulls RTK Query / analytics and can circularize
// browser test graphs that only need EventOrigin/track (see pickDataAccess).
import { type BimQueryIdSet, probeBimQuery, searchBimQueryIdSet } from "apis/procore/bimQuery";
import { inPredicate, or } from "apis/procore/searchPredicate";
import type { AppDispatch } from "app/store";
import { byProperty, type ObjectDataAccess } from "dataAccess";
import { bimIndexBitmapIds } from "features/bimIndex/session";

import { renderActions } from "./renderSlice";

const IFC_SPACE_VALUES = ["IfcOpeningElement", "IfcSpace", "IfcSpatialZone"] as const;
/** Procore /search is case-insensitive, so one casing covers Rooms/rooms and Areas/areas. */
const CATEGORY_VALUES = ["Rooms", "Areas"] as const;

const SEARCHES = [
    { property: "IfcClass", values: IFC_SPACE_VALUES },
    { property: "Element/IfcClass", values: IFC_SPACE_VALUES },
    { property: "Category", values: CATEGORY_VALUES },
    { property: "Element/Category", values: CATEGORY_VALUES },
] as const;

/**
 * Object-db equality is case-sensitive (`pdfObjectIds.ts`). Procore /search is not.
 * Include both casings seen in Novorender scenes so an always-on path is not a silent no-op.
 */
const OBJECT_DB_IFC_SPACE_VALUES = [...IFC_SPACE_VALUES, "ifcopeningelement", "ifcspace", "ifcspatialzone"] as const;
const OBJECT_DB_CATEGORY_VALUES = ["Rooms", "rooms", "Areas", "areas"] as const;

const OBJECT_DB_SEARCHES = [
    { property: "IfcClass", values: OBJECT_DB_IFC_SPACE_VALUES },
    { property: "Element/IfcClass", values: OBJECT_DB_IFC_SPACE_VALUES },
    { property: "Category", values: OBJECT_DB_CATEGORY_VALUES },
    { property: "Element/Category", values: OBJECT_DB_CATEGORY_VALUES },
] as const;

/** Same ceiling as BimIndexDataAccess.matchIdSets — resume widens, does not page. */
const MAX_RESUMED_READS = 10;

/** How long we wait for the served index probe before falling back to bim-search-db. */
const BIM_INDEX_PROBE_TIMEOUT_MS = 4000;

/**
 * Session fetch cache.
 * - Procore: keyed by company:project:scene:sceneVersion
 * - Novorender: keyed by `novorender:${sceneId}`
 * Fetch runs once from init; `spacesFrozenObjectIds` in renderSlice is the live apply
 * target and survives reset/home (no refetch). This Map + inflight only avoid duplicate
 * network work if ensure is invoked more than once for the same key.
 */
const spacesObjectIdsByScene = new Map<string, number[]>();
/** Dedupes concurrent ensures for the same cache key. */
const inflightFetchesByScene = new Map<string, Promise<number[]>>();

/**
 * Only results for this cache key may land in `spacesFrozenObjectIds`.
 * Cleared on each scene init so a late resolve for scene A cannot overwrite scene B
 * (object ids are small integers that collide across scenes).
 */
let activeFreezeApplyKey: string | null = null;

export type SpacesSceneContext = {
    companyId: number;
    projectId: number;
    sceneId: string;
    sceneVersionId: string;
};

function cacheKey({ companyId, projectId, sceneId, sceneVersionId }: SpacesSceneContext): string {
    return `${companyId}:${projectId}:${sceneId}:${sceneVersionId}`;
}

function novorenderCacheKey(sceneId: string): string {
    return `novorender:${sceneId}`;
}

export function getCachedSpacesObjectIds(sceneContext: SpacesSceneContext): number[] {
    return spacesObjectIdsByScene.get(cacheKey(sceneContext)) ?? [];
}

function setCachedObjectIds(key: string, objectIds: number[]): void {
    spacesObjectIdsByScene.set(key, Array.from(new Set(objectIds)));
}

/** Test helper — clears the in-memory session cache. */
export function clearSpacesObjectIdsCache(): void {
    spacesObjectIdsByScene.clear();
    inflightFetchesByScene.clear();
    activeFreezeApplyKey = null;
}

/**
 * Drop any in-flight freeze apply. Call when a new scene init starts (before scheduling
 * discovery) so a previous scene's late result cannot write into Redux.
 */
export function invalidateSpacesFreezeApply(): void {
    activeFreezeApplyKey = null;
}

/**
 * Prefer served BIM index: one OR of the four property variants, answered as a roaring
 * bitmap (`application/vnd.procore.bim.roaring`) — same Accept header
 * `BimIndexDataAccess.findIdsByProperties` uses under the hood.
 *
 * `PropertyQuery` / `findIdsByProperties` is AND-only across properties, so a
 * cross-property OR cannot go through that helper; `searchBimQueryIdSet` + `or(...)`
 * is the one-request equivalent.
 *
 * Returns `null` when the index is unavailable so the caller can fall back.
 */
async function fetchSpacesObjectIdsViaBimIndex(
    sceneContext: SpacesSceneContext,
    signal?: AbortSignal,
): Promise<number[] | null> {
    let available = false;
    try {
        // Timeout only — do not compose with caller `signal` via AbortSignal.any:
        // a shared abort would cancel the whole feature when only the probe should give up.
        available = await probeBimQuery(sceneContext, {
            signal: AbortSignal.timeout(BIM_INDEX_PROBE_TIMEOUT_MS),
        });
    } catch (e) {
        console.info("[spacesTransparency] path=bim-search-db (bim-index probe failed)", e);
        return null;
    }
    if (!available) {
        console.info("[spacesTransparency] path=bim-search-db (served bim-index unavailable)");
        return null;
    }

    console.info("[spacesTransparency] path=bim-index-roaring-or (single OR + roaring bitmap)");

    try {
        const predicate = or(...SEARCHES.map((search) => inPredicate(search.property, [...search.values])));
        const sets: BimQueryIdSet[] = [];
        let resumeToken: string | undefined;

        for (let read = 1; ; read++) {
            const set = await searchBimQueryIdSet(sceneContext, { predicate, resumeToken, signal });
            sets.push(set);
            resumeToken = set.resumeToken;
            if (!resumeToken) {
                break;
            }
            if (read >= MAX_RESUMED_READS) {
                console.warn(
                    `[spacesTransparency] bim-index search still incomplete after ${read} resumed reads; results may be incomplete`,
                );
                break;
            }
        }

        if (sets.length === 0) {
            return [];
        }

        const ids = await bimIndexBitmapIds(sets.map((set) => set.bytes));
        return Array.from(ids);
    } catch (e) {
        console.info("[spacesTransparency] path=bim-search-db (bim-index search failed; falling back)", e);
        return null;
    }
}

async function fetchSpacesObjectIdsViaBimSearchDb(
    sceneContext: SpacesSceneContext,
    signal?: AbortSignal,
): Promise<number[]> {
    console.info("[spacesTransparency] path=bim-search-db (parallel v2.0 /search)");

    const fetchObjectIdsBySearch = async (search: (typeof SEARCHES)[number]): Promise<number[]> => {
        const ids: number[] = [];
        for await (const result of searchProcoreObjects({
            ...sceneContext,
            filter: {
                searchPattern: [
                    {
                        property: search.property,
                        value: [...search.values],
                        exact: true,
                    },
                ],
            },
            signal,
        })) {
            if (Number.isFinite(result.id)) {
                ids.push(result.id);
            }
        }
        return ids;
    };

    const results = await Promise.all(SEARCHES.map((search) => fetchObjectIdsBySearch(search)));
    return Array.from(new Set(results.flat()));
}

/** Procore: bim-index when available, otherwise v2.0 /search. */
export async function fetchSpacesObjectIds(sceneContext: SpacesSceneContext, signal?: AbortSignal): Promise<number[]> {
    const fromIndex = await fetchSpacesObjectIdsViaBimIndex(sceneContext, signal);
    if (fromIndex !== null) {
        return fromIndex;
    }
    return fetchSpacesObjectIdsViaBimSearchDb(sceneContext, signal);
}

/**
 * Novorender standalone: ObjectDB property search only (no bim-index).
 * Same four IfcClass / Category variants as the Procore path (Rooms and Areas), merged and deduped.
 * Per-property failures are warned and skipped so one 500 does not drop the other hits.
 */
export async function fetchSpacesObjectIdsViaObjectDb(
    access: ObjectDataAccess,
    signal?: AbortSignal,
): Promise<number[]> {
    console.info("[spacesTransparency] path=object-db (parallel findIdsByProperties)");

    const settled = await Promise.allSettled(
        OBJECT_DB_SEARCHES.map(async (search) => {
            const ids = await access.findIdsByProperties(byProperty(search.property, [...search.values]), { signal });
            return Array.from(ids);
        }),
    );

    const ids: number[] = [];
    for (const [index, result] of settled.entries()) {
        if (result.status === "fulfilled") {
            ids.push(...result.value);
            continue;
        }
        const search = OBJECT_DB_SEARCHES[index];
        console.warn(
            `[spacesTransparency] object-db search failed property=${search?.property ?? index}`,
            result.reason,
        );
    }
    return Array.from(new Set(ids));
}

function applySpacesFrozen(dispatch: AppDispatch, objectIds: number[], forKey: string): void {
    if (objectIds.length === 0) {
        return;
    }
    if (activeFreezeApplyKey !== forKey) {
        return;
    }
    console.info(`[spacesTransparency] apply=frozen-filter (GPU unload) count=${objectIds.length}`);
    dispatch(renderActions.setSpacesFrozenObjectIds(objectIds));
}

/**
 * Spaces/rooms to put in the frozen (`filter`) highlight group.
 * Excludes the current selection so object-tree picks can reload and show those nodes
 * (Procore places frozen last, so subsequent-highlight exclusion alone is not enough).
 */
export function filterSpacesIdsForFreeze(
    spacesObjectIds: readonly number[],
    highlightedIds: { has: (id: number) => boolean },
    mainObject: number | undefined,
): number[] {
    if (spacesObjectIds.length === 0) {
        return [];
    }
    return spacesObjectIds.filter((objectId) => !highlightedIds.has(objectId) && objectId !== mainObject);
}

async function ensureFrozenForKey(
    key: string,
    fetchIds: () => Promise<number[]>,
    dispatch: AppDispatch,
): Promise<void> {
    // Claim apply ownership for this scene before any await so a prior scene's
    // in-flight resolve cannot land after invalidate + this claim.
    activeFreezeApplyKey = key;
    try {
        const cachedObjectIds = spacesObjectIdsByScene.get(key) ?? [];
        if (cachedObjectIds.length > 0) {
            applySpacesFrozen(dispatch, cachedObjectIds, key);
            return;
        }

        let inflight = inflightFetchesByScene.get(key);
        if (!inflight) {
            inflight = fetchIds().finally(() => {
                inflightFetchesByScene.delete(key);
            });
            inflightFetchesByScene.set(key, inflight);
        }

        const objectIds = await inflight;
        if (objectIds.length === 0) {
            return;
        }
        applySpacesFrozen(dispatch, objectIds, key);
        // Cache even if apply was skipped (stale scene) so a later ensure for this
        // same key can re-apply from cache without refetching.
        setCachedObjectIds(key, objectIds);
    } catch (e) {
        console.warn("Failed to apply spaces/rooms freeze:", e);
    }
}

/** Procore: fetch (once per scene version per page load) + freeze spaces/rooms out of GPU. Never throws. */
export async function ensureSpacesRoomsFrozen(sceneContext: SpacesSceneContext, dispatch: AppDispatch): Promise<void> {
    await ensureFrozenForKey(cacheKey(sceneContext), () => fetchSpacesObjectIds(sceneContext), dispatch);
}

/**
 * Novorender standalone: ObjectDB discovery + freeze. Always on (no host flag). Never throws.
 * Call after `dataAccessPromise` resolves; fire-and-forget from init.
 */
export async function ensureSpacesRoomsFrozenFromObjectDb(
    access: ObjectDataAccess,
    sceneId: string,
    dispatch: AppDispatch,
): Promise<void> {
    await ensureFrozenForKey(novorenderCacheKey(sceneId), () => fetchSpacesObjectIdsViaObjectDb(access), dispatch);
}
